import { withAlpha, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import { clamp, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { StatBarVisual, Theme } from "../themes/Theme.js";

export type StatBarOrientation = "horizontal" | "vertical";

export interface StatBarOptions extends ControlOptions {
  value?: number;
  maximum?: number;
  /** A shield or overheal pool drawn past the main fill. */
  shield?: number;
  /**
   * Split the fill into this many notches, the way a stamina wheel or a
   * segmented health bar does. 0 draws one continuous fill.
   */
  segments?: number;
  orientation?: StatBarOrientation;
  /** Main fill color; null takes the theme's accent. */
  fill?: Color | null;
  /** Color of the shield pool past the fill. */
  shieldFill?: Color | null;
  /** Color of the trailing "chip damage" fill. */
  chipFill?: Color | null;
  /** Seconds the trailing fill takes to catch up. 0 disables it. */
  chipSeconds?: number;
  /** Pause before the trailing fill starts moving, in seconds. */
  chipDelaySeconds?: number;
  /** Pulse the bar below this fraction of the maximum. 0 never pulses. */
  lowThreshold?: number;
  /** Draw "84 / 120" over the bar. */
  showText?: boolean;
  textFormat?: (value: number, maximum: number) => string;
  font?: FontSpec | null;
}

const DEFAULT_CHIP_SECONDS = 0.55;
const DEFAULT_CHIP_DELAY_S = 0.35;
const SEGMENT_GAP = 2;
const PULSE_SECONDS = 0.9;

/**
 * A health, mana or stamina bar.
 *
 * Three things separate this from `ProgressBar`, and each of them is why a
 * game cannot use one:
 *
 * - It runs **vertically** as well as horizontally. A mana bar down the side
 *   of the screen is not an exotic request.
 * - It has a **trailing fill**. When the value drops, the main fill snaps to
 *   the new value and a second, dimmer fill slides down to meet it after a
 *   beat. That lag is the whole reason a player can tell a big hit from a
 *   small one at a glance, and it is worth more than any amount of chrome.
 * - It has a **shield pool** past the fill and optional **segments** across it.
 *
 * The trailing fill only ever chases downward. Healing is instant, because
 * being told about a heal after the fact is no use to anyone.
 */
export class StatBar extends Control {
  #value: number;
  #maximum: number;
  #shield: number;
  /** Where the trailing fill has got to, in value units. */
  #chip: number;
  /** Frame time the value last dropped, so the chip can wait a beat. */
  #droppedAt = -1;
  /** Frame time of the last step, to measure this one against. */
  #steppedAt = -1;
  #animating = false;

  segments: number;
  orientation: StatBarOrientation;
  fill: Color | null;
  shieldFill: Color | null;
  chipFill: Color | null;
  chipSeconds: number;
  chipDelaySeconds: number;
  lowThreshold: number;
  showText: boolean;
  textFormat: ((value: number, maximum: number) => string) | null;
  font: FontSpec | null;

  constructor(options: StatBarOptions = {}) {
    super({ width: 180, height: 16, tabStop: false, ...options });
    this.#maximum = Math.max(0, options.maximum ?? 100);
    this.#value = clamp(options.value ?? this.#maximum, 0, this.#maximum);
    this.#shield = Math.max(0, options.shield ?? 0);
    this.#chip = this.#value;
    this.segments = Math.max(0, Math.floor(options.segments ?? 0));
    this.orientation = options.orientation ?? "horizontal";
    this.fill = options.fill ?? null;
    this.shieldFill = options.shieldFill ?? null;
    this.chipFill = options.chipFill ?? null;
    this.chipSeconds = options.chipSeconds ?? DEFAULT_CHIP_SECONDS;
    this.chipDelaySeconds = options.chipDelaySeconds ?? DEFAULT_CHIP_DELAY_S;
    this.lowThreshold = options.lowThreshold ?? 0;
    this.showText = options.showText ?? false;
    this.textFormat = options.textFormat ?? null;
    this.font = options.font ?? null;
  }

  // --- value ---------------------------------------------------------------

  get value(): number {
    return this.#value;
  }
  set value(v: number) {
    const next = clamp(Number.isFinite(v) ? v : this.#value, 0, this.#maximum);
    if (next === this.#value) return;
    const dropped = next < this.#value;
    this.#value = next;
    if (dropped) {
      // The chip stays where it was and starts its wait. Only the drop starts
      // a new one, so a run of small hits reads as one long drain rather than
      // resetting the animation on every tick.
      this.#droppedAt = this.desktop?.frameTime ?? 0;
      this.#syncAnimation();
    } else {
      // Healing is instant: a bar that crept up would be telling the player
      // about something that already happened.
      this.#chip = next;
    }
    this.emit("valuechanged");
    this.emit("change");
    this.invalidate();
  }

  get maximum(): number {
    return this.#maximum;
  }
  set maximum(v: number) {
    const next = Math.max(0, Number.isFinite(v) ? v : this.#maximum);
    if (next === this.#maximum) return;
    this.#maximum = next;
    this.#value = clamp(this.#value, 0, next);
    this.#chip = clamp(this.#chip, 0, next);
    this.invalidate();
  }

  /** An absorb pool drawn past the fill; not counted in `value`. */
  get shield(): number {
    return this.#shield;
  }
  set shield(v: number) {
    const next = Math.max(0, Number.isFinite(v) ? v : this.#shield);
    if (next === this.#shield) return;
    this.#shield = next;
    this.invalidate();
  }

  /** Where the trailing fill has got to, in value units. */
  get chipValue(): number {
    return this.#chip;
  }

  /** Filled proportion, 0..1. */
  get fraction(): number {
    return this.#maximum > 0 ? this.#value / this.#maximum : 0;
  }

  get chipFraction(): number {
    return this.#maximum > 0 ? this.#chip / this.#maximum : 0;
  }

  /** True while the bar is low enough to be pulsing for attention. */
  get isLow(): boolean {
    return this.lowThreshold > 0 && this.fraction <= this.lowThreshold;
  }

  /** Set value and maximum together, e.g. on respawn or a level-up. */
  setValue(value: number, maximum: number): void {
    this.maximum = maximum;
    this.value = value;
    this.#chip = this.#value;
    this.#droppedAt = -1;
    this.#syncAnimation();
  }

  /** Drop the trailing fill onto the value at once, skipping the slide. */
  settle(): void {
    this.#chip = this.#value;
    this.#droppedAt = -1;
    this.#syncAnimation();
    this.invalidate();
  }

  // --- animation -----------------------------------------------------------

  /**
   * Slide the trailing fill towards the value.
   *
   * `step` is a real delta rather than an assumed frame length, clamped so a
   * tab that was in the background for a minute does not resume by having the
   * bar teleport.
   */
  #advance(now: number): void {
    const step = this.#steppedAt < 0 ? 0 : Math.min(MAX_STEP_SECONDS, now - this.#steppedAt);
    this.#steppedAt = now;
    if (this.#chip <= this.#value) {
      this.#chip = this.#value;
      return;
    }
    if (this.chipSeconds <= 0) {
      this.#chip = this.#value;
      return;
    }
    if (now - this.#droppedAt < this.chipDelaySeconds) return;
    // Constant rate in bar-widths per second, so a scratch and a near-death
    // hit drain at the same speed and differ only in how far they go.
    const perSecond = this.#maximum / this.chipSeconds;
    this.#chip = Math.max(this.#value, this.#chip - perSecond * step);
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#chip > this.#value && this.chipSeconds > 0;
    const pulsing = this.isLow;
    const next = want || pulsing;
    if (next === this.#animating) return;
    this.#animating = next;
    if (next) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    this.#animating = false;
    this.#syncAnimation();
  }

  // --- painting ------------------------------------------------------------

  /** What the theme needs to know to frame this bar. */
  visual(): StatBarVisual {
    return {
      fraction: this.fraction,
      chipFraction: this.chipFraction,
      shieldFraction: this.#maximum > 0 ? this.#shield / this.#maximum : 0,
      segments: this.segments,
      vertical: this.orientation === "vertical",
      low: this.isLow,
      disabled: !this.effectivelyEnabled,
    };
  }

  /** The box the range `from`..`to` of the bar occupies inside `r`. */
  fillBox(r: Rect, from: number, to: number): Rect {
    const lo = clamp(Math.min(from, to), 0, 1);
    const hi = clamp(Math.max(from, to), 0, 1);
    if (this.orientation === "vertical") {
      // Vertical bars drain downward: full is the top.
      return {
        x: r.x,
        y: r.y + r.height * (1 - hi),
        width: r.width,
        height: r.height * (hi - lo),
      };
    }
    return { x: r.x + r.width * lo, y: r.y, width: r.width * (hi - lo), height: r.height };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const now = this.desktop?.frameTime ?? 0;
    this.#advance(now);
    this.#syncAnimation();

    const p = theme.palette;
    const radius = Math.min(theme.metrics.controlCornerRadius, Math.min(this.width, this.height) / 2);
    const outer = this.localRect;
    const inner: Rect = { x: 1, y: 1, width: this.width - 2, height: this.height - 2 };

    theme.drawStatBarFrame(rc, outer, this.visual());

    const base = this.fill ?? p.accent;
    // The trailing fill sits behind the main one and only shows where the
    // main one has already gone.
    if (this.#chip > this.#value) {
      rc.fillRect(
        this.fillBox(inner, this.fraction, this.chipFraction),
        this.chipFill ?? withAlpha(base, 0.4),
        radius,
      );
    }

    let ink = base;
    if (this.isLow) {
      // A slow pulse rather than a flash: it has to be noticeable in the
      // corner of the eye without pulling the eye off the fight.
      const phase = (now % PULSE_SECONDS) / PULSE_SECONDS;
      ink = withAlpha(base, 0.65 + 0.35 * Math.abs(1 - phase * 2));
    }
    if (this.#value > 0) rc.fillRect(this.fillBox(inner, 0, this.fraction), ink, radius);

    if (this.#shield > 0 && this.#maximum > 0) {
      const from = this.fraction;
      const to = Math.min(1, from + this.#shield / this.#maximum);
      rc.fillRect(this.fillBox(inner, from, to), this.shieldFill ?? p.selectionBg, radius);
    }

    if (this.segments > 1) this.#paintSegments(rc, inner, p.controlBgDisabled);
    if (this.showText) this.#paintText(rc, theme, outer);
  }

  /** Notch the bar into segments by drawing the gaps between them. */
  #paintSegments(rc: RenderContext, r: Rect, gap: Color): void {
    const vertical = this.orientation === "vertical";
    const span = vertical ? r.height : r.width;
    const step = span / this.segments;
    if (step <= SEGMENT_GAP) return;
    for (let i = 1; i < this.segments; i++) {
      const at = i * step - SEGMENT_GAP / 2;
      rc.fillRect(
        vertical
          ? { x: r.x, y: r.y + at, width: r.width, height: SEGMENT_GAP }
          : { x: r.x + at, y: r.y, width: SEGMENT_GAP, height: r.height },
        gap,
      );
    }
  }

  #paintText(rc: RenderContext, theme: Theme, r: Rect): void {
    const font = this.font ?? theme.metrics.font;
    const text = this.textFormat
      ? this.textFormat(this.#value, this.#maximum)
      : `${Math.round(this.#value)} / ${Math.round(this.#maximum)}`;
    const width = rc.measureText(text, font).width;
    rc.drawText(text, r.x + (r.width - width) / 2, r.y + r.height / 2, font, theme.palette.text, {
      baseline: "middle",
    });
  }
}

/** Longest step the chip fill will take in one frame, in seconds. */
const MAX_STEP_SECONDS = 0.05;
